import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Typography, Button, Box, MenuItem, FormControl } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Modal from "../Modal/Modal";
import { TextField, CheckboxWithLabel, Select } from "formik-mui";
import { Field } from "formik";
import { useTranslation } from "react-i18next";
import HTMLEditor from "../HtmlEditor/HtmlEditor";

const useStyles = makeStyles(theme => ({
  numQuestionsField: {
    width: 96
  }
}));

const QuizOrSurveyModalForm = ({
  submitForm,
  values,
  errors,
  onClose,
  open,
  isSubmitting,
  isNewVersion,
  isEditing,
  setSubmitting,
  resetForm,
  setFieldValue,
  type,
  loading
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  useEffect(() => {
    resetForm();
  }, [open, values.questionType]);

  // Once the form is submitted and loading becomes false, re-enable the form
  useEffect(() => {
    if (isSubmitting && !loading) {
      setSubmitting(false);
    }
  }, [loading, isSubmitting]);

  // When the user unchecks "Randomize Quiz Questions", remove the value for # of questions served
  useEffect(() => {
    if (!values.randomlyOrdered) {
      setFieldValue("numQuestions", "");
    }
  }, [values.randomlyOrdered]);

  const handleNumCharactersChange = numCharacters => {
    setFieldValue("instructionsTextOnlyLength", numCharacters);
  };

  const createOrEditDialogTitle = isEditing
    ? t(`courseBuilder.editQuizModalTitle.${type}`)
    : t(`courseBuilder.createQuizModalTitle.${type}`);

  const dialogTitle = isNewVersion
    ? t(`courseBuilder.editQuizModalTitle.${type}`)
    : createOrEditDialogTitle;

  const saveBtnLabel = isEditing
    ? t("courseBuilder.editQuizSaveAndEditBtn")
    : t("courseBuilder.createQuizSaveAndAddBtn");

  return (
    <Form data-qa="create-quiz-form">
      <Modal
        scrollable
        disableEnforceFocus
        showCloseIcon
        maxWidth="lg"
        fullWidth
        dialogTitle={
          <Typography variant="h4" color="textPrimary">
            {dialogTitle}
          </Typography>
        }
        dialogContent={
          <Box display="flex" flexDirection="column" mb={1}>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              mb={1.5}
              mt={1}
            >
              <Box flex={1} mb={3}>
                <Field
                  variant="filled"
                  fullWidth
                  label={t("courseBuilder.createQuizTitleLabel")}
                  component={TextField}
                  name="name"
                  inputProps={{ "data-qa": "create-quiz-title-field" }}
                />
              </Box>
            </Box>
            <Box mb={-2}>
              <Typography variant="subtitle1" color="textSecondary">
                {t("courseBuilder.createQuizInstructionsLabel")}
              </Typography>
            </Box>
            <HTMLEditor
              height={189}
              disabled={isSubmitting}
              onChange={value => {
                setFieldValue("instructions", value, false);
              }}
              value={values.instructions}
              menubar={false}
              plugins={["lists", "link", "image", "media", "code"]}
              toolbar="undo redo | bold italic underline | alignment numlist bullist outdent indent | link"
              errorMessage={errors.instructions}
              characterLimit={600}
              onNumCharactersChange={handleNumCharactersChange}
            />
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "flex-start" }}
            >
              <Box display="flex" flexDirection="column" flex={1}>
                <Field
                  component={CheckboxWithLabel}
                  name="randomlyOrdered"
                  inputProps={{
                    "data-qa": "create-quiz-randomize-questions-field"
                  }}
                  Label={{
                    label: (
                      <Typography variant="subtitle1">
                        {t(`courseBuilder.createQuizRandomlyOrdered.${type}`)}
                      </Typography>
                    )
                  }}
                  color="primary"
                  type="checkbox"
                />
                <Box display="flex" flexDirection="column" mt={2} ml={"37px"}>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="body2">
                      {t("courseBuilder.createQuizNumQuestionsLabel")}
                    </Typography>
                    <Box mt={2}>
                      <Field
                        hiddenLabel
                        variant="filled"
                        component={TextField}
                        name="numQuestions"
                        disabled={!values.randomlyOrdered}
                        type="number"
                        InputProps={{
                          classes: { root: classes.numQuestionsField },
                          inputProps: {
                            min: "1",
                            "data-qa": "create-quiz-num-questions-field"
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                {type === "survey" && (
                  <Box mt={2}>
                    <Field
                      component={CheckboxWithLabel}
                      name="anonymousReporting"
                      Label={{
                        label: (
                          <Typography variant="subtitle1">
                            {t("courseBuilder.createQuizAnonymousReporting")}
                          </Typography>
                        )
                      }}
                      color="primary"
                      type="checkbox"
                    />
                  </Box>
                )}
              </Box>
              <Box
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                flex={1}
                alignItems={{ xs: "flex-start", md: "center" }}
                ml={{ xs: "37px", md: 0 }}
                my={{ xs: 4, md: 0 }}
              >
                <Box mr={2} mb={{ xs: 2, md: 0 }}>
                  <Typography variant="subtitle1" noWrap>
                    {t("courseBuilder.createQuizQuestionsPerPageLabel")}
                  </Typography>
                </Box>
                <FormControl fullWidth>
                  <Field
                    type="text"
                    name="questionsPerPage"
                    component={Select}
                    inputProps={{
                      name: "questionsPerPage",
                      id: "questionsPerPage"
                    }}
                    variant="filled"
                    fullWidth
                    hiddenLabel
                  >
                    <MenuItem value="all">
                      {t("courseBuilder.createQuizQuestionsPerPageAllOption")}
                    </MenuItem>
                    <MenuItem value="1">1</MenuItem>
                    <MenuItem value="5">5</MenuItem>
                    <MenuItem value="10">10</MenuItem>
                  </Field>
                </FormControl>
              </Box>
            </Box>
          </Box>
        }
        dialogActions={
          <>
            <Button color="primary" disabled={isSubmitting} onClick={onClose}>
              {t("courseBuilder.createQuizCancelBtn")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={submitForm}
              disabled={isSubmitting}
            >
              {saveBtnLabel}
            </Button>
          </>
        }
        open={open}
        onClose={onClose}
      />
    </Form>
  );
};
const getCreateQuizOrSurveySchema = t => {
  return Yup.object().shape({
    name: Yup.string()
      .required(t("validationMessages.titleRequired"))
      .max(
        150,
        t("validationMessages.cannotExceedLength", {
          type: "Title",
          maxLength: 150
        })
      ),
    instructions: Yup.mixed().test(
      "validUrl",
      t("validationMessages.cannotExceedLength", {
        type: "Instructions",
        maxLength: 600
      }),
      function() {
        return (
          !this.parent.instructions ||
          this.parent.instructionsTextOnlyLength <= 600
        );
      }
    ),
    numQuestions: Yup.mixed().test(
      "validNumber",
      t("validationMessages.validNumber"),
      function() {
        return (
          !this.parent.randomlyOrdered ||
          (!!this.parent.numQuestions && this.parent.numQuestions > 0) ||
          !this.parent.numQuestions
        );
      }
    )
  });
};

const QuizOrSurveyModal = ({
  onClose,
  open,
  item,
  onSubmit,
  type,
  quizOrSurveyData,
  isEditing,
  isNewVersion,
  loading,
  versionId,
  shortCourseId,
  errors
}) => {
  const { t } = useTranslation();
  return (
    <Formik
      initialValues={{
        name: quizOrSurveyData ? quizOrSurveyData.name : "",
        instructions: quizOrSurveyData ? quizOrSurveyData.instructions : "",
        randomlyOrdered: quizOrSurveyData
          ? quizOrSurveyData.randomlyOrdered
          : false,
        numQuestions:
          quizOrSurveyData && quizOrSurveyData.settings
            ? quizOrSurveyData.settings.numQuestions
            : "",
        anonymousReporting:
          quizOrSurveyData && quizOrSurveyData.settings
            ? quizOrSurveyData.settings.anonymousReporting
            : false,
        questionsPerPage:
          quizOrSurveyData && quizOrSurveyData.settings
            ? quizOrSurveyData.settings.questionsPerPage
            : "all",
        versionId: versionId,
        courseSectionId: shortCourseId,
        quizType: type,
        instructionsTextOnlyLength: 0
      }}
      validationSchema={getCreateQuizOrSurveySchema(t)}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        delete values.instructionsTextOnlyLength;
        onSubmit(values);
      }}
    >
      {({
        submitForm,
        isSubmitting,
        setSubmitting,
        values,
        resetForm,
        setFieldValue,
        errors
      }) => (
        <QuizOrSurveyModalForm
          setSubmitting={setSubmitting}
          submitForm={submitForm}
          isSubmitting={isSubmitting}
          values={values}
          isEditing={isEditing}
          resetForm={resetForm}
          isNewVersion={isNewVersion}
          open={open}
          onClose={onClose}
          setFieldValue={setFieldValue}
          errors={errors}
          type={type}
          quizOrSurveyData={quizOrSurveyData}
          loading={loading}
          versionId={versionId}
        />
      )}
    </Formik>
  );
};

QuizOrSurveyModal.propTypes = {
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to create the quiz/survey
   */
  onSubmit: PropTypes.func,
  /**
   * Either "quiz" or "survey"
   */
  type: PropTypes.string,
  loading: PropTypes.bool,
  isEditing: PropTypes.bool,
  isNewVersion: PropTypes.bool,
  versionId: PropTypes.string,
  shortCourseId: PropTypes.string
};

export default QuizOrSurveyModal;
